import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Users, 
  GripVertical,
  FileText,
  Search,
  MapPin,
  Droplet
} from 'lucide-react';
import { clientsAPI, usersAPI, serviceCallsAPI } from '@shared/apiClient';
import toast from 'react-hot-toast';
import ClientDetailsModal from '../components/ClientDetailsModal';
import ClientFormModal from '../components/ClientFormModal';
import { formatCspireAssignmentDisplayName } from '../utils/cspireDisplay';

const getClientDisplayName = (client) => {
  if (!client) return 'Unknown Customer';
  if (client.isCommercial && client.companyName) return client.companyName;
  const lastName = client.contactLastName?.trim();
  const firstName = client.contactFirstName?.trim();
  if (lastName && firstName) return `${lastName}, ${firstName}`;
  return lastName || firstName || client.companyName || 'Unknown Customer';
};

const TechnicianColumn = ({ technician, assignments, onAssignmentClick, onEditNotes, draggedAssignment, onAssignmentDragStart, onAssignmentDragEnd, onDrop, onDragOver, isDraggingOver }) => {
  const assignmentCount = assignments.length;

  return (
    <div
      className={`flex flex-col bg-white rounded-lg border-2 shadow-sm min-h-[600px] ${
        isDraggingOver ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
      } transition-colors`}
      onDrop={onDrop}
      onDragOver={onDragOver}
    >
      {/* Column Header */}
      <div className="flex-shrink-0 bg-gray-50 border-b border-gray-200 px-4 py-3 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-gray-900 truncate">
              {technician.firstName} {technician.lastName}
            </h3>
            <p className="text-xs text-gray-500 truncate">{technician.email}</p>
          </div>
          <span className="px-2.5 py-1 text-sm font-medium bg-blue-100 text-blue-800 rounded-full flex-shrink-0 ml-2">
            {assignmentCount}
          </span>
        </div>
      </div>

      {/* Assignments List */}
      <div className="flex-1 p-3 overflow-y-auto">
        {assignments.length === 0 ? (
          <div className="text-center py-12 text-sm text-gray-400">
            <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No assignments</p>
          </div>
        ) : (
          <div className="space-y-2">
            {assignments.map((assignment) => {
              const client = assignment.client;
              const cspireWorkOrder = assignment.cspireWorkOrder;
              const franklinSite = assignment.franklinSite;
              const isCspire = assignment.isCspire && cspireWorkOrder;
              const isFranklin = !!franklinSite || assignment.isFranklin;
              if (!client && !cspireWorkOrder && !franklinSite) return null;
              
              const isDragging = draggedAssignment?.id === assignment.id;
              const hasNotes = assignment.customerNotes && assignment.customerNotes.trim().length > 0;
              const isPriority = assignment.isPriority === true;
              const currentMonth = new Date().getMonth() + 1;
              const isOilChangeMonth = client && client.oilChangeMonth === currentMonth;
              const displayName = isFranklin
                ? `Franklin - ${franklinSite?.locationName || franklinSite?.siteId || 'Site'}`
                : isCspire
                ? formatCspireAssignmentDisplayName(cspireWorkOrder)
                : getClientDisplayName(client);
              const location = isFranklin
                ? ([franklinSite?.city, franklinSite?.state].filter(Boolean).join(', ') || 'Franklin Site')
                : isCspire
                ? (cspireWorkOrder.site?.city && cspireWorkOrder.site?.state
                  ? `${cspireWorkOrder.site.city}, ${cspireWorkOrder.site.state}`
                  : (cspireWorkOrder.locationName || cspireWorkOrder.site?.locationName || 'CSpire Site'))
                : `${client.city}, ${client.state}`;
              const isDraggable = !!client || isCspire || isFranklin;
              
              return (
                <div
                  key={assignment.id}
                  draggable={isDraggable}
                  onDragStart={(e) => {
                    if (!isDraggable) return;
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', assignment.id);
                    onAssignmentDragStart?.(assignment);
                  }}
                  onDragEnd={onAssignmentDragEnd}
                  onClick={() => {
                    if (!client) return;
                    onAssignmentClick(assignment);
                  }}
                  className={`bg-white border-2 rounded-lg p-3 cursor-pointer hover:shadow-md transition-all ${
                    isDragging ? 'opacity-50' : 
                    isPriority ? 'border-red-300 hover:border-red-500' : 'border-gray-300 hover:border-blue-500'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5 cursor-move" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {displayName}
                        </p>
                        {isCspire && (
                          <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-800 rounded">
                            CSpire
                          </span>
                        )}
                        {isFranklin && (
                          <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-800 rounded">
                            Franklin
                          </span>
                        )}
                        {isPriority && (
                          <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                            Repair
                          </span>
                        )}
                        {isOilChangeMonth && (
                          <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded flex items-center gap-1">
                            <Droplet className="h-3 w-3" />
                            Oil
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPin className="h-3 w-3" />
                        <p className="truncate">{location}</p>
                      </div>
                      {hasNotes && (
                        <p className="text-xs text-gray-600 italic truncate mt-1" title={assignment.customerNotes}>
                          📝 {assignment.customerNotes}
                        </p>
                      )}
                    </div>
                    {client && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditNotes(assignment);
                        }}
                        className="flex-shrink-0 p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors"
                        title="Edit notes"
                      >
                        <FileText className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const AddNotesModal = ({ client, technician, isOpen, onClose, onConfirm, existingNotes = '', isEdit = false }) => {
  const [notes, setNotes] = useState(existingNotes);

  useEffect(() => {
    if (isOpen) {
      setNotes(existingNotes);
    }
  }, [isOpen, existingNotes]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(notes);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-gray-50 px-4 py-5 sm:px-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {isEdit ? 'Edit Notes' : 'Add Notes to Assignment'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <FileText className="h-6 w-6" />
                </button>
              </div>
              <div className="mt-2">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{getClientDisplayName(client)}</span>
                  <span className="mx-2">•</span>
                  {client?.city}, {client?.state}
                </p>
                {technician && (
                  <p className="text-sm text-gray-500 mt-1">
                    {isEdit ? 'Assigned to' : 'Assigning to'}: {technician.firstName} {technician.lastName}
                  </p>
                )}
              </div>
            </div>

            <div className="px-4 py-5 sm:p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any special instructions or notes for the technician..."
                  autoFocus
                />
                <p className="mt-1 text-xs text-gray-500">
                  These notes will be visible to the technician when they view this assignment.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
              <button
                type="submit"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {isEdit ? 'Update Notes' : 'Confirm'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default function TechAssignments() {
  const [searchQuery, setSearchQuery] = useState('');
  const [draggedAssignment, setDraggedAssignment] = useState(null);
  const [draggingOverColumn, setDraggingOverColumn] = useState(null);
  const [selectedClientForModal, setSelectedClientForModal] = useState(null);
  const [isClientDetailsModalOpen, setIsClientDetailsModalOpen] = useState(false);
  const [clientToEdit, setClientToEdit] = useState(null);
  const [isClientFormModalOpen, setIsClientFormModalOpen] = useState(false);
  const [pendingAssignment, setPendingAssignment] = useState(null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);

  const queryClient = useQueryClient();

  // Fetch technicians
  const { data: techniciansData } = useQuery(
    'technicians',
    () => usersAPI.getUsers({ role: 'technician', limit: 100 })
  );

  // Fetch current assignments
  const { data: weeklyAssignmentsData } = useQuery(
    'weeklyAssignments',
    () => clientsAPI.getWeeklyAssignments(),
    {
      refetchInterval: 30000,
      refetchOnWindowFocus: true
    }
  );

  const technicians = techniciansData?.data?.users?.filter(tech => tech.isActive) || [];

  // Group assignments by technician
  const assignmentsByTechnician = useMemo(() => {
    const assignments = weeklyAssignmentsData?.data?.assignments || [];
    const grouped = {};
    
    assignments.forEach(assignment => {
      const techId = assignment.technicianId;
      if (!grouped[techId]) {
        grouped[techId] = [];
      }
      grouped[techId].push(assignment);
    });

    return grouped;
  }, [weeklyAssignmentsData]);

  // Filter technicians by search
  const filteredTechnicians = useMemo(() => {
    if (!searchQuery.trim()) return technicians;
    
    const query = searchQuery.toLowerCase();
    return technicians.filter(tech => {
      const name = `${tech.firstName} ${tech.lastName}`.toLowerCase();
      const email = (tech.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [technicians, searchQuery]);

  const handleAssignmentDragStart = (assignment) => {
    setDraggedAssignment(assignment);
  };

  const handleDragEnd = () => {
    setDraggedAssignment(null);
    setDraggingOverColumn(null);
  };

  const handleDragOver = (e, technicianId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggingOverColumn(technicianId);
  };

  const handleDrop = async (e, technicianId) => {
    e.preventDefault();
    setDraggingOverColumn(null);

    if (!draggedAssignment || !technicianId) return;

    const assignmentToReassign = draggedAssignment;
    setDraggedAssignment(null);

    if (assignmentToReassign.isCspire && assignmentToReassign.cspireWorkOrder) {
      try {
        await serviceCallsAPI.updateServiceCall(assignmentToReassign.id, {
          technicianId: String(technicianId)
        });
        toast.success('CSpire work order reassigned!');
        queryClient.invalidateQueries('weeklyAssignments');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to reassign CSpire work order');
      }
      return;
    }

    if (assignmentToReassign.isFranklin && assignmentToReassign.franklinSite) {
      try {
        await serviceCallsAPI.updateServiceCall(assignmentToReassign.id, {
          technicianId: String(technicianId)
        });
        toast.success('Franklin site reassigned!');
        queryClient.invalidateQueries('weeklyAssignments');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to reassign Franklin site');
      }
      return;
    }

    const client = assignmentToReassign.client;
    if (!client || !client.id) {
      toast.error('Customer information is missing');
      return;
    }

    try {
      const requestData = {
        clientId: String(client.id),
        technicianId: String(technicianId),
        notes: assignmentToReassign.customerNotes || ''
      };
      
      await clientsAPI.assignContractClient(requestData);
      toast.success('Assignment updated!');
      queryClient.invalidateQueries('weeklyAssignments');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reassign');
    }
  };

  const handleAssignmentClick = (assignment) => {
    if (!assignment.client) return;
    setSelectedClientForModal(assignment.client);
    setIsClientDetailsModalOpen(true);
  };

  const handleEditClient = (client) => {
    setClientToEdit(client);
    setIsClientFormModalOpen(true);
  };

  const handleEditNotes = (assignment) => {
    const technician = technicians.find(t => t.id === assignment.technicianId);
    setPendingAssignment({
      type: 'edit',
      client: assignment.client,
      technician,
      technicianId: assignment.technicianId,
      assignmentId: assignment.id,
      existingNotes: assignment.customerNotes || ''
    });
    setIsNotesModalOpen(true);
  };

  const handleUpdateNotes = async (notes) => {
    if (!pendingAssignment) return;

    try {
      await serviceCallsAPI.updateServiceCall(pendingAssignment.assignmentId, {
        customerNotes: notes || null
      });
      toast.success('Notes updated successfully!');
      queryClient.invalidateQueries('weeklyAssignments');
      setIsNotesModalOpen(false);
      setPendingAssignment(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update notes');
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tech Assignments</h1>
            <p className="mt-1 text-sm text-gray-600">
              View and manage all technician assignments
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search technicians..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tech Columns Grid */}
      <div className="flex-1 overflow-auto bg-gray-50 p-6">
        {filteredTechnicians.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">
              {searchQuery ? 'No technicians match your search.' : 'No active technicians found.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredTechnicians.map((technician) => {
              const assignments = assignmentsByTechnician[technician.id] || [];
              
              return (
                <TechnicianColumn
                  key={technician.id}
                  technician={technician}
                  assignments={assignments}
                  onAssignmentClick={handleAssignmentClick}
                  onEditNotes={handleEditNotes}
                  draggedAssignment={draggedAssignment}
                  onAssignmentDragStart={handleAssignmentDragStart}
                  onAssignmentDragEnd={handleDragEnd}
                  onDrop={(e) => handleDrop(e, technician.id)}
                  onDragOver={(e) => handleDragOver(e, technician.id)}
                  isDraggingOver={draggingOverColumn === technician.id}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Client Details Modal */}
      <ClientDetailsModal
        client={selectedClientForModal}
        isOpen={isClientDetailsModalOpen}
        onClose={() => {
          setIsClientDetailsModalOpen(false);
          setSelectedClientForModal(null);
        }}
        onEdit={handleEditClient}
      />

      {/* Client Form Modal (for editing) */}
      <ClientFormModal
        client={clientToEdit}
        isOpen={isClientFormModalOpen}
        mode="edit"
        onClose={() => {
          setIsClientFormModalOpen(false);
          setClientToEdit(null);
        }}
      />

      {/* Edit Notes Modal */}
      {pendingAssignment && (
        <AddNotesModal
          client={pendingAssignment.client}
          technician={pendingAssignment.technician}
          isOpen={isNotesModalOpen}
          onClose={() => {
            setIsNotesModalOpen(false);
            setPendingAssignment(null);
          }}
          onConfirm={handleUpdateNotes}
          existingNotes={pendingAssignment.existingNotes || ''}
          isEdit={pendingAssignment.type === 'edit'}
        />
      )}
    </div>
  );
}

